import React, { useState, useEffect, useRef, useMemo } from "react"
import * as dateHelper from "../../booking/helpers/date"
import * as endpoints from "../../api/endpoints"
import ReactHtmlParser from "react-html-parser"
import { isMobile, isIOS } from "react-device-detect"
import da from "date-fns/locale/da"
import enGB from "date-fns/locale/en-GB"
import Svg from "../_svgs/svg"
import SearchOverlay from "./search-overlay"
import reactStringReplace from "react-string-replace"
import * as endpoint from "../../booking/constants/Endpoints"
import axios from "axios"
import OutsideClickTrigger from "../../booking/components/common/OutsideClickTrigger"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import * as cookieHelper from "../../helpers/cookie"
import { DateTime } from "luxon"
import useWindowWidth from "../../hooks/useWindowWidth"

const FlightSearchHero = (props) => {
  const dtLocale = dateHelper.luxonLocale(props.locale)
  const zone = "Europe/Copenhagen"
  const cphTimeConvert = DateTime.local().setLocale(dtLocale).setZone(zone)
  const cphTime = cphTimeConvert.setZone("local", { keepLocalTime: true })

  const startDate = cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 })
  const minDate = startDate.minus({ days: 1 })
  const minTime = cphTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
  const maxTime = cphTime.set({ hours: 23, minutes: 0, seconds: 0, milliseconds: 0 })

  const [flights, setFlights] = useState([])
  const [direction, setDirection] = useState("D")
  const [q, setQ] = useState("")
  const [showOverlay, setShowOverlay] = useState(false)
  const [searchDate, setSearchDate] = useState(startDate)
  const [datepickerOpen, setDatepickerOpen] = useState(false)
  const [renderWaitingTime, setRenderWaitingTime] = useState(false)
  const [t2WaitingTime, setT2WaitingTime] = useState("N/A")
  const [t3WaitingTime, setT3WaitingTime] = useState("N/A")
  const [airports, setAirports] = useState([])
  const [airlines, setAirlines] = useState([])
  const [scrollStart, setScrollStart] = useState(0)
  const [overInput, setOverInput] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)

  const windowWidth = useWindowWidth() // hook to re-render on resize

  const [dayMinTime, setDayMinTime] = useState(minTime)
  const waitingTimeIntervalRef = useRef(null)
  const focusedElementRef = useRef(null)

  const resultsRef = useRef(null)
  const resultsUpdateTimeout = useRef(null)
  const containerRef = useRef(null)

  const resetInputButtonRef = useRef(null)
  const searchBarRef = useRef(null)

  useEffect(() => {
    const handleKeyDown = (e) => {
      const flightId = document.getElementById("flight-search-hero-input")
      const flightIsFocused = document.activeElement === flightId

      if (e.key === "Escape") {
        setShowOverlay(false)
        if (isMobile) {
          const elements = document.getElementsByClassName("site-header")
          const siteHeader = elements[0]
          if (siteHeader) {
            siteHeader.style.display = null
            const siteHeaderWrapper = document.getElementsByClassName("site-header-wrapper")[0]
            if (siteHeaderWrapper) {
              siteHeaderWrapper.style.height = siteHeader.clientHeight + "px"
            }
          }
          setZIndexOfNotificationBanner(null)
          document.body.classList.remove("disable-scroll")
        }
        const input = document.querySelector("#flight-search-hero-input")
        if (input) {
          input.blur()
        }
      }

      if (e.key === "Enter" && flightIsFocused) {
        window.location.href = `${
          direction === "D" ? props.departuresPage : props.arrivalsPage
        }?q=${q}&date=${searchDate.toFormat("dd / MM / yyyy")}&time=${searchDate.toFormat("HH")}`
      }
    }

    document.body.addEventListener("keydown", handleKeyDown)

    setRenderWaitingTime(true)
    getWaitingTime()
    waitingTimeIntervalRef.current = setInterval(getWaitingTime, 10000)

    return () => {
      document.body.removeEventListener("keydown", handleKeyDown)
      if (waitingTimeIntervalRef.current) {
        clearInterval(waitingTimeIntervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const handleExitFlightSearchForm = (event) => {
      if (event.key === "Escape") {
        setShowOverlay(false)
        const input = document.querySelector("#flight-search-hero-input")
        if (input) {
          input.blur()
        }
      }
    }

    document.addEventListener("keydown", handleExitFlightSearchForm)

    return () => {
      document.removeEventListener("keydown", handleExitFlightSearchForm)
    }
  }, [])

  useEffect(() => {
    const handleFocusIn = (event) => {
      const relatedTarget = event.target

      // exceptions to focus rule
      if (containerRef.current?.contains(relatedTarget) || searchBarRef.current === relatedTarget || resetInputButtonRef.current === relatedTarget) {
        return
      }

      setShowOverlay(false)
      setDatepickerOpen(false)
      document.body.classList.remove("disable-scroll")

      if (isMobile) {
        const elements = document.getElementsByClassName("site-header")
        const siteHeader = elements[0]
        if (siteHeader) {
          siteHeader.style.display = null
          const siteHeaderWrapper = document.getElementsByClassName("site-header-wrapper")[0]
          if (siteHeaderWrapper) {
            siteHeaderWrapper.style.height = siteHeader.clientHeight + "px"
          }
        }
      }
    }
    document.addEventListener("focusin", handleFocusIn)

    return () => {
      document.removeEventListener("focusin", handleFocusIn)
    }
  }, [])

  const updateResultsMessage = () => {
    if (q.length < props.minCharsBeforeSearch) return
    const shortDateOptions = {
      month: "short",
      day: "2-digit",
    }
    const shortSearchDate = new Date(searchDate).toLocaleDateString(props.locale.code, shortDateOptions)

    if (flights && flights.length === 0) {
      const formattedNoResultsLabel = props.noResultsLabel.formatUnicorn({ input: q, date: shortSearchDate })
      resultsUpdateTimeout.current = setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.innerHTML = `<p lang="${props.locale}">${formattedNoResultsLabel}</p>`
        }
      }, 2000)
      return
    }

    if (!props.ariaLiveUpdateMessage) return

    if (resultsUpdateTimeout.current) {
      clearTimeout(resultsUpdateTimeout.current)
    }

    resultsUpdateTimeout.current = setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.innerHTML = `<p lang="${props.locale}">${props.ariaLiveUpdateMessage.formatUnicorn({ results: flights.length })}</p>`
      }
    }, 2000)
  }

  useEffect(() => {
    updateResultsMessage()
    return () => {
      if (resultsUpdateTimeout.current) {
        clearTimeout(resultsUpdateTimeout.current)
      }
    }
  }, [flights])

  const search = async (query) => {
    if (query.length >= (props.minCharsBeforeSearch || 2)) {
      const dt = searchDate
      const zone = "Europe/Copenhagen"
      const cphTime = dt.setZone(zone, { keepLocalTime: true })
      const start = cphTime.toUTC()
      const end = cphTime.endOf("day").toUTC()

      const flightsData = {
        direction: direction,
        query: query,
        startDateTime: start,
        endDateTime: end,
        language: props.locale,
      }

      try {
        const [flightsResult, airportsResult] = await Promise.all([getFlights(flightsData), getAirports(flightsData)])

        setFlights(flightsResult || [])
        setAirports(airportsResult || [])
        setAirlines(getAirlines(flightsResult))
      } catch (error) {
        console.log(error)
      }
    }
  }

  const getFlights = (data) => {
    return endpoints
      .getFlightInfoTable(data)
      .then((res) => res.data || [])
      .catch((err) => {
        console.log(err)
        return []
      })
  }

  const getAirports = (data) => {
    return endpoints
      .getAirportsFromFlights(data)
      .then((res) => res.data || [])
      .catch((err) => {
        console.log(err)
        return []
      })
  }

  const getAirlines = (flights) => {
    if (flights && flights.length > 0) {
      const result = Array.from(new Set(flights.map((f) => f.IataAirline))).map((iata) => {
        const matchFlight = flights.find((f) => f.IataAirline === iata)
        return {
          iata,
          name: matchFlight.Airline,
          nameMobile: matchFlight.AirlineMobile,
        }
      })
      return result
    }
    return []
  }

  const handleInput = (event, doSearch = true) => {
    const newVal = event.target.value
    setQ(newVal)

    if (newVal.length >= 1) {
      if (isMobile && !showOverlay) {
        window.scrollTo(0, 0) // fix position on iOS
        setZIndexOfNotificationBanner(0)
      }
      setShowOverlay(true)
      if (doSearch) search(newVal)

      if (isMobile) {
        const elements = document.getElementsByClassName("site-header")
        const siteHeader = elements[0]
        if (siteHeader) {
          siteHeader.style.display = "none"
        }
        document.body.classList.add("disable-scroll")
      }
    } else {
      if (!isMobile) {
        handleCloseOverlay()
      }
    }
  }

  const handleDirectionChange = (dir) => {
    setDirection(dir)
  }

  useEffect(() => {
    search(q)
  }, [direction])

  const handleCloseOverlay = () => {
    searchBarRef.current.focus()
    setShowOverlay(false)
    setSearchDate(startDate)
    setQ("")
    setFlights([])
    setAirports([])

    if (isMobile) {
      const elements = document.getElementsByClassName("site-header")
      const siteHeader = elements[0]
      if (siteHeader) {
        siteHeader.style.display = null
        const siteHeaderWrapper = document.getElementsByClassName("site-header-wrapper")[0]
        if (siteHeaderWrapper) {
          siteHeaderWrapper.style.height = siteHeader.clientHeight + "px"
        }
      }
      setZIndexOfNotificationBanner(null)
      document.body.classList.remove("disable-scroll")
    }
  }

  const handleDateChange = (dateInput) => {
    setSearchDate((prev) => {
      let dateTime = DateTime.fromJSDate(dateInput)
      const startOfPrevDate = prev.startOf("day")
      const startOfMinDate = minDate.startOf("day")
      const startOfNewDate = dateTime.startOf("day")

      if (startOfMinDate.toMillis() === startOfNewDate.toMillis()) {
        if (dateTime <= minDate || dateTime.toFormat("HH:mm") === prev.toFormat("HH:mm")) {
          return minDate
        }
        return dateTime
      } else {
        if (startOfPrevDate.toMillis() !== startOfNewDate.toMillis()) {
          dateTime = dateTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
          const timeList = document.getElementsByClassName("react-datepicker__time-list")[0]
          if (timeList) {
            timeList.scrollTo({
              top: 0,
              behavior: "smooth",
            })
          }
        }
        setDayMinTime(minTime) // reset dayMinTime to the default
        return dateTime
      }
    })
    setTimeout(() => {
      search(q)
    }, 0)
  }

  const handleDatepicker = () => {
    setDatepickerOpen(!datepickerOpen)
  }

  const closeDatepicker = () => {
    console.log("closing datepicker")
    setDatepickerOpen(false)
  }

  const getWaitingTime = () => {
    focusedElementRef.current = document.activeElement

    axios
      .get(endpoint.WAITING_TIME_SECURITY(props.hosts))
      .then((result) => {
        if (result.status === 204) {
          setT2WaitingTime("N/A")
          setT3WaitingTime("N/A")
          return
        }
        setT2WaitingTime(result.data.t2WaitingTimeInterval)
        setT3WaitingTime(result.data.t3WaitingTimeInterval)
      })
      .catch((error) => {
        setT2WaitingTime("N/A")
        setT3WaitingTime("N/A")
        console.log(error)
      })
      .finally(() => {
        if (focusedElementRef.current) {
          focusedElementRef.current.focus()
        }
      })
  }

  const getWaitingTimeString = (text, input) => {
    const newText = text.replace("{time}", input)
    const regex = new RegExp(/\((.*?)\)/)
    return reactStringReplace(newText, regex, (match, i) => <span key={match + i}>({match})</span>)
  }

  const handleOutsideClick = () => {
    if (!overInput) {
      setShowOverlay(false)
      setDatepickerOpen(false)
      console.log("outside click triggered")
    }
  }

  const handleInputFocus = () => {
    if (q.length > 0) {
      if (isMobile && !showOverlay) {
        window.scrollTo(0, 0)
        setZIndexOfNotificationBanner(0)
      }
      setShowOverlay(true)
      if (isMobile) {
        const elements = document.getElementsByClassName("site-header")
        const siteHeader = elements[0]
        if (siteHeader) {
          siteHeader.style.display = "none"
        }
        document.body.classList.add("disable-scroll")
      }
    }
  }

  const handleTouchStart = () => {
    if (isIOS) {
      setScrollStart(window.pageYOffset)
    }
  }

  const handleTouchEnd = (e) => {
    if (isIOS) {
      const end = window.pageYOffset
      const distance = end - scrollStart
      if (distance === 0) {
        // Only if you haven't scrolled
        e.target.focus()
      }
      setScrollStart(0)
    }
  }

  const handleMouseLeave = (e) => {
    setOverInput(false)
    if (isIOS) {
      e.target.blur()
    }
  }

  const handleInputClick = (e) => {
    e.target.focus()
  }

  const setZIndexOfNotificationBanner = (zindex) => {
    const notificationBanner = document.querySelector(".notification")
    if (notificationBanner) {
      const notificationVersion = notificationBanner.dataset.notificationVersion
      const notificationCookie = cookieHelper.getCookie(notificationVersion)
      if (notificationCookie !== "closed") {
        notificationBanner.style.zIndex = zindex
      }
    }
  }

  let heroStyle = {}
  let renderHeroImage = false
  if (isMobile && props.backgroundImageMobile) {
    heroStyle = { backgroundImage: `url(${props.backgroundImageMobile})` }
    renderHeroImage = true
  } else if (props.backgroundImage) {
    heroStyle = { backgroundImage: `url(${props.backgroundImage})` }
    renderHeroImage = true
  }

  const waitingTimeSection = useMemo(() => {
    if (!renderWaitingTime) return null
    return (
      <div className="secwait">
        <div className="headline">{props.waitingTimeAtSecurity || "Ventetid i security"}</div>
        {getWaitingTimeString(props.terminalTwoWaitingLabel || "Terminal 2 ({time})", t2WaitingTime)}
        &nbsp;&nbsp;&nbsp;
        {getWaitingTimeString(props.terminalThreeWaitingLabel || "Terminal 3 ({time})", t3WaitingTime)}
      </div>
    )
  }, [renderWaitingTime, props.waitingTimeAtSecurity, props.terminalTwoWaitingLabel, props.terminalThreeWaitingLabel, t2WaitingTime, t3WaitingTime])

  const topPadding = SelectionFactoryHelper.getFromSelectionFactory(props.topPadding)
  const bottomPadding = SelectionFactoryHelper.getFromSelectionFactory(props.bottomPadding)
  const bottomMargin = SelectionFactoryHelper.getFromSelectionFactory(props.bottomMargin)

  return (
    <>
      {renderHeroImage ? (
        <img
          style={{ display: "none" }}
          loading="eager"
          fetchPriority="high"
          src={isMobile ? props.backgroundImageMobile : props.backgroundImage}
          alt=""
        />
      ) : null}

      <section
        className={`deck deck--front ${topPadding ? topPadding : ""} ${bottomPadding ? bottomPadding : ""} ${bottomMargin ? bottomMargin : ""}`}
        style={heroStyle}
      >
        <header className="section-header section-header-search">
          <div className="section-header__inner">
            <div className="infohead__white">{props.title}</div>
            <h1>{ReactHtmlParser(props.heading)}</h1>
            <div className="form">
              <div className="form__section form__section--light form__section--fat">
                <div ref={containerRef} className={`form__section__inner ${isMobile && showOverlay ? "is-active" : ""}`}>
                  <OutsideClickTrigger onOutsideClick={() => (isMobile ? undefined : handleOutsideClick())}>
                    <React.Fragment>
                      <div className="form__field form__field--text form__field--text-search form__field--text--no-label is-bound is-filled">
                        <span className="form__valid"></span>
                        <input
                          ref={searchBarRef}
                          className="form__item"
                          id="flight-search-hero-input"
                          autoComplete="off"
                          autoCapitalize="none"
                          name="q"
                          type="text"
                          placeholder={props.searchBoxPlaceholder}
                          value={q}
                          onChange={(e) => handleInput(e)}
                          onMouseEnter={() => setOverInput(true)}
                          onMouseLeave={(e) => handleMouseLeave(e)}
                          onFocus={() => {
                            handleInputFocus, setInputFocused(true)
                          }}
                          onBlur={() => {
                            setInputFocused(false)
                          }}
                          onClick={handleInputClick}
                          onTouchStart={handleTouchStart}
                          onTouchEnd={handleTouchEnd}
                          aria-controls="hero-live-region"
                        />
                        <div
                          tabIndex="-1"
                          lang={props.locale}
                          ref={resultsRef}
                          id="results"
                          aria-live="polite"
                          className="visually-hidden"
                          aria-hidden={!inputFocused}
                        ></div>

                        {!isMobile ? (
                          <button
                            ref={resetInputButtonRef}
                            tabIndex={q.length >= props.minCharsBeforeSearch && showOverlay ? "0" : "-1"}
                            className={`search-button${showOverlay ? " active" : ""}`}
                            onClick={showOverlay ? handleCloseOverlay : undefined}
                            aria-label={props.clearSearchAriaLabel}
                          >
                            <div className="search-icon"></div>
                          </button>
                        ) : null}

                        {isMobile && showOverlay ? (
                          <button ref={resetInputButtonRef} onClick={handleCloseOverlay}>
                            <Svg label={props.clearSearchAriaLabel} iconName={"close"} fillColor={"#071143"} />
                          </button>
                        ) : null}
                      </div>

                      <SearchOverlay
                        {...props}
                        flights={flights}
                        airports={airports}
                        airlines={airlines}
                        direction={direction}
                        q={q}
                        searchDate={searchDate}
                        minDate={minDate}
                        minTime={dayMinTime}
                        maxTime={maxTime}
                        showOverlay={showOverlay}
                        datepickerOpen={datepickerOpen}
                        handleDirectionChange={handleDirectionChange}
                        handleDateChange={handleDateChange}
                        locale={props.locale === "da" ? da : enGB}
                        handleDatepicker={handleDatepicker}
                        closeDatepicker={closeDatepicker}
                        showTime={!isMobile}
                      />
                    </React.Fragment>
                  </OutsideClickTrigger>
                </div>
              </div>
            </div>

            {waitingTimeSection}
          </div>

          <div className="button-container">
            {props.arrivalsButtonLabel && props.arrivalsPage && (
              <a href={props.arrivalsPage}>
                <div className="button button--medium has-icon no-shadow">
                  <Svg iconName={"planetakeoff"} fillColor={"#064FC2"} />
                  <span className="text">{props.arrivalsButtonLabel}</span>
                </div>
              </a>
            )}
            {props.departuresButtonLabel && props.departuresPage && (
              <a href={props.departuresPage}>
                <div className="button button--medium has-icon no-shadow">
                  <Svg iconName={"planetakeoff"} fillColor={"#064FC2"} />
                  <span className="text">{props.departuresButtonLabel}</span>
                </div>
              </a>
            )}
          </div>
        </header>
      </section>
    </>
  )
}

export default FlightSearchHero
